/**
 * JSON-RPC 2.0 client over WebSocket for real-time active visitors.
 *
 * Protocol:
 *   Client sends:  { jsonrpc:"2.0", method, params?, id? }
 *   Server sends:  { jsonrpc:"2.0", result|error, id }   (responses)
 *                  { jsonrpc:"2.0", method, params }      (notifications)
 */

import { logger } from './logger';

// ── Types ────────────────────────────────────────────────────────────────────

export type VisitorStatus = 'inside' | 'awaiting' | 'expired' | 'exited' | 'revoked' | 'pending_approval';

export interface WsVisitorData {
  id: string;
  name: string;
  unit: string;
  unit_id?: string;
  street_name?: string;
  vehicle: string;
  entryTime: string;
  exitTime?: string;
  status: VisitorStatus;
  photo?: string;
  personId: string;
  roleId: string;
  roleType: 'visitor' | 'service_provider';
  access_type?: string;
  /** Mirrors VisitorData.PeriodAccess (services/visitor_ws_hub.go) — see OperationalVisitor. */
  period_access?: boolean;
  /** Mirrors VisitorData.Liberacao (models/liberacao.go). */
  liberacao?: { status: string; kind: string; until?: string; revoked_at?: string };
  entry_window_expires_at?: string;
  access_expires_at?: string;
  valid_until?: string;
  first_entry_at?: string;
  last_entry_at?: string;
  last_exit_at?: string;
  role_created_at?: string;
  responsible_person_id?: string;
  authorized_by_name?: string;
}

export interface WsVisitorSummary {
  personId: string;
  roleId: string;
  status: VisitorStatus;
  last_exit_at?: string;
  first_entry_at?: string;
  last_entry_at?: string;
  entry_window_expires_at?: string;
}

export interface WsVisitorPagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface WsCustomerNote {
  id: string;
  customer_id: string;
  unit_id?: string;
  department_id?: string;
  person_id?: string;
  content: string;
  is_active: boolean;
  created_by_id: string;
  created_by_name: string;
  created_at: string;
  concluded_by_id?: string;
  concluded_by_name?: string;
  concluded_at?: string;
  concluded_reason?: string;
}

export interface WsVisitorUpdate {
  type: 'snapshot' | 'update';
  visitors: WsVisitorData[];
  allSummary: WsVisitorSummary[];
  notes: WsCustomerNote[];
  pagination: WsVisitorPagination;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface WsStats {
  messagesReceived: number;
  messagesSent: number;
  lastReceivedAt: Date | null;
  lastSentAt: Date | null;
  connectedAt: Date | null;
  reconnectCount: number;
}

// ── Internal JSON-RPC types ───────────────────────────────────────────────────

interface RpcRequest {
  jsonrpc: '2.0';
  method: string;
  params?: unknown;
  id?: number;
}

interface RpcResponse {
  jsonrpc: '2.0';
  result?: unknown;
  error?: { code: number; message: string };
  id?: number;
}

interface RpcNotification {
  jsonrpc: '2.0';
  method: string;
  params: WsVisitorUpdate;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;

// ── Client ────────────────────────────────────────────────────────────────────

type UpdateCallback = (data: WsVisitorUpdate) => void;
type StatusCallback = (status: ConnectionStatus) => void;

export class VisitorWebSocketClient {
  private ws: WebSocket | null = null;
  private idCounter = 0;
  private pendingRequests = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();

  private onUpdateCallback: UpdateCallback | null = null;
  private onStatusCallback: StatusCallback | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;

  private backoff = INITIAL_BACKOFF_MS;
  private destroyed = false;

  private _status: ConnectionStatus = 'disconnected';
  private _url = '';
  private _token = '';

  private _stats: WsStats = {
    messagesReceived: 0,
    messagesSent: 0,
    lastReceivedAt: null,
    lastSentAt: null,
    connectedAt: null,
    reconnectCount: 0,
  };

  // ── Public API ────────────────────────────────────────────────────────────

  get status(): ConnectionStatus {
    return this._status;
  }

  getStats(): WsStats {
    return { ...this._stats };
  }

  onUpdate(cb: UpdateCallback): void {
    this.onUpdateCallback = cb;
  }

  onStatusChange(cb: StatusCallback): void {
    this.onStatusCallback = cb;
  }

  connect(url: string, token: string): void {
    this._url = url;
    this._token = token;
    this.destroyed = false;
    this._doConnect();
  }

  disconnect(): void {
    this.destroyed = true;
    this._clearTimers();
    if (this.ws) {
      this.ws.close(1000, 'client disconnect');
      this.ws = null;
    }
    this._setStatus('disconnected');
    // Reject all pending requests
    this.pendingRequests.forEach(({ reject }) => reject(new Error('Disconnected')));
    this.pendingRequests.clear();
  }

  /** Tell the server to subscribe to visitor updates for this page/pageSize/statusFilter. */
  subscribe(page: number, pageSize: number, statusFilter = ''): void {
    this._send({ method: 'visitors.subscribe', params: { page, pageSize, statusFilter } });
  }

  /** Tell the server to deliver a different page/filter. Server pushes the update via notification. */
  paginate(page: number, pageSize: number, statusFilter = ''): void {
    this._send({ method: 'visitors.paginate', params: { page, pageSize, statusFilter } });
  }

  /** Fire a ping and return a promise that resolves when pong arrives. */
  ping(): Promise<void> {
    return this._call('ping').then(() => undefined);
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private _setStatus(s: ConnectionStatus): void {
    if (this._status === s) return;
    this._status = s;
    this.onStatusCallback?.(s);
  }

  private _doConnect(): void {
    if (this.destroyed) return;
    this._setStatus('connecting');

    const url = `${this._url}?token=${encodeURIComponent(this._token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.backoff = INITIAL_BACKOFF_MS;
      this._stats.connectedAt = new Date();
      this._setStatus('connected');
      this._startPing();
      logger.info('VisitorWsClient', 'Connected');
    };

    ws.onmessage = (evt) => {
      this._handleMessage(evt.data as string);
    };

    ws.onerror = (evt) => {
      logger.warn('VisitorWsClient', 'WebSocket error', evt);
    };

    ws.onclose = (evt) => {
      this._stopPing();
      this.ws = null;

      if (this.destroyed) return;

      this._stats.reconnectCount++;
      this._stats.connectedAt = null;
      logger.warn('VisitorWsClient', `Closed (${evt.code}) — reconnecting in ${this.backoff}ms`);
      this._setStatus('reconnecting');
      this._scheduleReconnect();
    };
  }

  private _scheduleReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this._doConnect();
    }, this.backoff);
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
  }

  private _handleMessage(raw: string): void {
    this._stats.messagesReceived++;
    this._stats.lastReceivedAt = new Date();

    let msg: RpcResponse | RpcNotification;
    try {
      msg = JSON.parse(raw);
    } catch {
      logger.warn('VisitorWsClient', 'Failed to parse message', { raw });
      return;
    }

    // Notification (no id): server-pushed visitor update
    // RpcNotification has `method` but no `id`; RpcResponse has `id` but no `method`
    if ('method' in msg) {
      const notif = msg as RpcNotification;
      if (notif.method === 'visitors.updated' && notif.params) {
        this.onUpdateCallback?.(notif.params);
      }
      return;
    }

    // Response to a call we made
    const resp = msg as RpcResponse;
    if (resp.id != null) {
      const pending = this.pendingRequests.get(resp.id);
      if (pending) {
        this.pendingRequests.delete(resp.id);
        if (resp.error) {
          pending.reject(new Error(`RPC error ${resp.error.code}: ${resp.error.message}`));
        } else {
          pending.resolve(resp.result);
        }
      }
      return;
    }

    // Server result that looks like a visitor update (response to visitors.subscribe/paginate)
    // The server sends the page as the `result` of the subscribe call
    if (resp.result && typeof resp.result === 'object') {
      const r = resp.result as WsVisitorUpdate;
      if (r.visitors && r.pagination) {
        this.onUpdateCallback?.(r);
      }
    }
  }

  private _send(req: Omit<RpcRequest, 'jsonrpc'>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const msg: RpcRequest = { jsonrpc: '2.0', ...req };
    this.ws.send(JSON.stringify(msg));
    this._stats.messagesSent++;
    this._stats.lastSentAt = new Date();
  }

  private _call(method: string, params?: unknown): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('Not connected'));
        return;
      }
      const id = ++this.idCounter;
      this.pendingRequests.set(id, { resolve, reject });
      const msg: RpcRequest = { jsonrpc: '2.0', method, id, ...(params ? { params } : {}) };
      this.ws.send(JSON.stringify(msg));
      this._stats.messagesSent++;
      this._stats.lastSentAt = new Date();
    });
  }

  private _startPing(): void {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      // Use _call (sends with an id) so the response can be matched and the
      // pongTimer is cleared. Using _send (no id) would drop the response.
      this.pongTimer = setTimeout(() => {
        logger.warn('VisitorWsClient', 'Pong timeout — reconnecting');
        this.ws?.close();
      }, PONG_TIMEOUT_MS);
      this._call('ping').then(() => {
        if (this.pongTimer) clearTimeout(this.pongTimer);
        this.pongTimer = null;
      }).catch(() => {
        // Connection closing — pongTimer or close handler will handle it
      });
    }, PING_INTERVAL_MS);
  }

  private _stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pingTimer = null;
    this.pongTimer = null;
  }

  private _clearTimers(): void {
    this._stopPing();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}
